import { useEffect } from "react";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

function ContactPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const contactInfo = [
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Our Location",
      details: ["Navi Mumbai 400703", "Maharashtra, India"],
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Phone Number",
      details: ["+91 99873 43532"],
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email Address",
      details: ["info@immaculate.ltd"],
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Working Hours",
      details: ["Mon - Fri: 9:00 AM - 6:00 PM", "Sat: 9:00 AM - 2:00 PM"],
    },
  ];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-brand-navy to-blue-900 py-16 sm:py-20 md:py-24 lg:py-20">
        <div className="container mx-auto px-5 sm:px-6 lg:px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-white/10 p-4 rounded-full">
                <Mail className="w-12 h-12 sm:w-16 sm:h-16 text-white" />
              </div>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-4 sm:mb-6">
              Contact Us
            </h1>
            <p className="font-body text-lg sm:text-xl text-white/90 leading-relaxed max-w-2xl mx-auto">
              Get in touch with us for any inquiries about our manpower and staffing solutions in Mumbai and across India. We're here to help!
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="bg-gray-50 py-12 sm:py-16">
        <div className="container mx-auto px-5 sm:px-6 lg:px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
              >
                <div className="text-gold mb-4">{info.icon}</div>
                <h3 className="font-display text-xl font-semibold text-brand-navy mb-3">
                  {info.title}
                </h3>
                <div className="space-y-1">
                  {info.details.map((detail, idx) => (
                    <p key={idx} className="text-text-dark text-sm">
                      {detail}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Map & Additional Info Section */}
      <section className="bg-white py-12 sm:py-16 md:py-20 lg:py-16">
        <div className="container mx-auto px-5 sm:px-6 lg:px-4">
          <div className="max-w-5xl mx-auto">
            <div className="space-y-8">
              {/* Map Placeholder */}
              <div className="bg-gray-200 rounded-lg overflow-hidden h-[400px] lg:h-[500px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d120615.72236456253!2d72.93573532453418!3d19.03268024819447!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c3d2e5b6b037%3A0x4b3f6f4c6e8e8a2f!2sNavi%20Mumbai%2C%20Maharashtra%20400703!5e0!3m2!1sen!2sin!4v1635000000000!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  title="IMMACULATE Location - Navi Mumbai"
                />
              </div>

              {/* Why Contact Us */}
              <div className="bg-gradient-to-br from-brand-navy to-blue-900 p-8 rounded-lg text-white">
                <h3 className="font-display text-2xl font-bold mb-4 text-white">
                  Why Choose IMMACULATE?
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-gold mr-2 mt-1">✓</span>
                    <span>Over multiple employees across multiple organizations</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-2 mt-1">✓</span>
                    <span>Presence in multiple locations across India</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-2 mt-1">✓</span>
                    <span>Industry-leading staffing solutions</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-2 mt-1">✓</span>
                    <span>24/7 customer support</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-2 mt-1">✓</span>
                    <span>Compliance with Indian labor laws</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gray-50 py-12 sm:py-16">
        <div className="container mx-auto px-5 sm:px-6 lg:px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-brand-navy mb-4">
              Ready to Build Your Perfect Team?
            </h2>
            <p className="text-text-dark text-lg mb-8">
              Whether you're looking for skilled professionals or seeking employment opportunities, 
              we're here to connect the right talent with the right opportunities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/#sectors">
                <Button className="bg-brand-navy hover:bg-brand-navy/90 text-white px-8 py-3 w-full sm:w-auto">
                  View Job Opportunities
                </Button>
              </a>
              <a href="https://wa.link/qyr374" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white px-8 py-3 w-full sm:w-auto">
                  Request Staffing Solutions
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ContactPage;

